/**
 * Debugging utilities for authentication.
 * Use these in browser console to debug auth issues.
 */

import { auth } from './auth';
import { log } from './logger';

/**
 * Debug function to check token status.
 * Run in browser console: window.debugAuth()
 */
export function debugAuth() {
  const token = auth.getToken();

  log.debug('AUTH', '--- Authentication Debug ---');

  if (!token) {
    log.debug('AUTH', 'No token found in localStorage');
    log.debug('AUTH', 'Token key:', 'auth_token');
    log.debug('AUTH', 'Action: User needs to authenticate via Strava OAuth');
    return;
  }

  log.debug('AUTH', 'Token found');
  log.debug('AUTH', 'Token length:', token.length);

  // Check token format
  const parts = token.split('.');
  log.debug('AUTH', 'Token parts:', parts.length, '(should be 3 for JWT)');

  if (parts.length !== 3) {
    log.error('AUTH', 'Invalid token format - should have 3 parts (header.payload.signature)');
    return;
  }

  // Decode and show token info
  try {
    const header = JSON.parse(atob(parts[0]));
    const payload = JSON.parse(atob(parts[1]));

    log.debug('AUTH', 'Token header:', header);
    log.debug('AUTH', 'Token payload:', payload);

    // Check expiration
    if (payload.exp) {
      const expDate = new Date(payload.exp * 1000);
      const now = new Date();
      const isExpired = now >= expDate;

      log.debug('AUTH', 'Expiration date:', expDate.toISOString());
      log.debug('AUTH', 'Current time:', now.toISOString());
      log.debug('AUTH', isExpired ? 'Token is EXPIRED' : 'Token is valid');

      if (isExpired) {
        log.debug('AUTH', 'Action: Token needs to be refreshed - reconnect Strava');
      }
    } else {
      log.warn('AUTH', 'Token has no expiration claim');
    }

    // Show other claims
    if (payload.athlete_id) {
      log.debug('AUTH', 'Athlete ID:', payload.athlete_id);
    }
    if (payload.user_id) {
      log.debug('AUTH', 'User ID:', payload.user_id);
    }

  } catch (error) {
    log.error('AUTH', 'Failed to decode token:', error);
  }

  // Check if token would be used
  const isLoggedIn = auth.isLoggedIn();
  log.debug('AUTH', 'auth.isLoggedIn():', isLoggedIn);

  const expiration = auth.getTokenExpiration();
  if (expiration) {
    log.debug('AUTH', 'Token expires:', expiration.toISOString());
  }
}

/**
 * Check Authorization header format.
 * Run in browser console: window.checkAuthHeader()
 */
export function checkAuthHeader() {
  const token = auth.getToken();

  if (!token) {
    log.debug('AUTH', 'No token - cannot check header format');
    return;
  }

  const headerValue = `Bearer ${token}`;
  log.debug('AUTH', '--- Authorization Header Check ---');
  log.debug('AUTH', 'Header format:', 'Authorization: Bearer <token>');
  log.debug('AUTH', 'Header value length:', headerValue.length);
  log.debug('AUTH', 'Starts with "Bearer ":', headerValue.startsWith('Bearer '));
  log.debug('AUTH', 'Token part:', token.substring(0, 20) + '...');
}

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as { debugAuth?: () => void; checkAuthHeader?: () => void }).debugAuth = debugAuth;
  (window as { debugAuth?: () => void; checkAuthHeader?: () => void }).checkAuthHeader = checkAuthHeader;
}
